import React, {
  Children,
  Fragment,
  createContext,
  forwardRef,
  isValidElement,
  lazy,
  Suspense,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { Autocomplete, useFilter } from 'react-aria-components/Autocomplete';
import { Button as AriaButton } from 'react-aria-components/Button';
import { DateInput, DateSegment } from 'react-aria-components/DateField';
import { DatePicker as AriaDatePicker } from 'react-aria-components/DatePicker';
import { Dialog } from 'react-aria-components/Dialog';
import { FieldError } from 'react-aria-components/FieldError';
import { Group } from 'react-aria-components/Group';
import { I18nProvider } from 'react-aria-components/I18nProvider';
import { Input } from 'react-aria-components/Input';
import { ListBox, ListBoxItem } from 'react-aria-components/ListBox';
import { Popover } from 'react-aria-components/Popover';
import { SearchField } from 'react-aria-components/SearchField';
import { Select as AriaSelect, SelectValue } from 'react-aria-components/Select';
import {
  CalendarDateTime,
  parseDate,
  parseDateTime,
  today,
  type DateValue,
} from '@internationalized/date';
import { Calendar as CalendarIcon, Check, ChevronDown, Search, X } from '@yingbaba/icons';
import './form-controls.css';

const CalendarPanel = lazy(() => import('./calendar-panel'));

export const FormFieldContext = createContext<{
  labelId: string;
  descriptionId?: string;
  invalid: boolean;
} | null>(null);

type ControlProps = {
  id?: string;
  name?: string;
  className?: string;
  style?: CSSProperties;
  disabled?: boolean;
  required?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
  onBlur?: () => void;
  validationBehavior?: 'native' | 'aria';
};
export type SelectOption = { value: string; label: ReactNode; disabled?: boolean };
type SelectProps = ControlProps & {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  children?: ReactNode;
  options?: SelectOption[];
  placeholder?: string;
  searchable?: boolean;
};

function text(nodes: ReactNode): string {
  return Children.toArray(nodes)
    .map((node) =>
      isValidElement<{ children?: ReactNode }>(node) ? text(node.props.children) : String(node),
    )
    .join('');
}
function readOptions(children: ReactNode): SelectOption[] {
  return Children.toArray(children).flatMap((node) => {
    if (
      !isValidElement<{ value?: string; children?: ReactNode; disabled?: boolean; label?: string }>(
        node,
      )
    )
      return [];
    if (node.type === Fragment) return readOptions(node.props.children);
    if (node.type !== 'option') return [];
    return [
      {
        value: String(node.props.value ?? text(node.props.children)),
        label: node.props.label || node.props.children,
        disabled: node.props.disabled,
      },
    ];
  });
}

export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
  {
    children,
    options,
    value,
    defaultValue,
    onChange,
    onBlur,
    id,
    name,
    className = '',
    style,
    disabled,
    required,
    placeholder = '请选择',
    searchable,
    validationBehavior = 'native',
    ...aria
  },
  ref,
) {
  const field = useContext(FormFieldContext);
  const rows = options || readOptions(children);
  const initial = defaultValue ?? rows.find((row) => !row.disabled)?.value ?? '';
  const [internal, setInternal] = useState(initial);
  const current = value ?? internal;
  const [open, setOpen] = useState(false);
  const [container, setContainer] = useState<Element>();
  const trigger = useRef<HTMLButtonElement>(null);
  useImperativeHandle(ref, () => trigger.current!, []);
  useEffect(() => {
    trigger.current?.closest('form')?.dispatchEvent(new Event('change', { bubbles: true }));
  }, [current]);
  useEffect(() => {
    if (value !== undefined) return;
    const form = trigger.current?.closest('form');
    const reset = () => setInternal(initial);
    form?.addEventListener('reset', reset);
    return () => form?.removeEventListener('reset', reset);
  }, [value, initial]);
  const { contains } = useFilter({ sensitivity: 'base' });
  const hasSearch = searchable ?? rows.length >= 8;
  const label = aria['aria-label'];
  const labelledBy = aria['aria-labelledby'] || (!label ? field?.labelId : undefined);
  const list = (
    <ListBox
      className="select-list"
      renderEmptyState={() => <div className="select-no-results">没有匹配的选项</div>}
    >
      {rows.map((row) => (
        <ListBoxItem
          key={row.value}
          id={row.value}
          textValue={text(row.label)}
          data-value={row.value}
          isDisabled={row.disabled}
          className="select-option"
        >
          {({ isSelected }) => (
            <>
              <span>{row.label}</span>
              <Check
                size={15}
                aria-hidden="true"
                style={{ visibility: isSelected ? 'visible' : 'hidden' }}
              />
            </>
          )}
        </ListBoxItem>
      ))}
    </ListBox>
  );
  return (
    <I18nProvider locale="zh-CN">
      <AriaSelect
        className={`form-select ${className.replace(/\binput\b/g, '').trim()}`}
        style={style}
        aria-label={label}
        aria-labelledby={labelledBy}
        aria-describedby={aria['aria-describedby'] || field?.descriptionId}
        name={name}
        value={current === '' && !rows.some((row) => row.value === '') ? null : current}
        onChange={(key) => {
          const next = key == null ? '' : String(key);
          setInternal(next);
          onChange?.(next);
        }}
        isDisabled={disabled}
        isRequired={required}
        isInvalid={
          aria['aria-invalid'] === true || aria['aria-invalid'] === 'true' || field?.invalid
        }
        validationBehavior={validationBehavior}
        validate={() => (required && !current ? '请选择一项后继续。' : null)}
        placeholder={placeholder}
        isOpen={open}
        onOpenChange={(next) => {
          setContainer(trigger.current?.closest('[role="dialog"]') || undefined);
          setOpen(next);
        }}
      >
        <AriaButton
          ref={trigger}
          id={id}
          className="form-select-trigger"
          onBlur={onBlur}
          data-value={current}
        >
          <SelectValue className="form-select-value">
            {({ selectedText }) => selectedText || placeholder}
          </SelectValue>
          <ChevronDown size={16} className="form-select-chevron" aria-hidden="true" />
        </AriaButton>
        <FieldError className="control-error" />
        <Popover
          className="control-popover select-popover"
          placement="bottom start"
          offset={6}
          UNSTABLE_portalContainer={container}
        >
          {hasSearch ? (
            <Autocomplete filter={contains}>
              <SearchField aria-label="搜索选项" className="select-search" autoFocus>
                <Search size={15} aria-hidden="true" />
                <Input placeholder="输入关键词筛选" />
                <AriaButton aria-label="清除选项搜索">
                  <X size={14} />
                </AriaButton>
              </SearchField>
              {list}
            </Autocomplete>
          ) : (
            list
          )}
        </Popover>
      </AriaSelect>
    </I18nProvider>
  );
});

type DatePickerProps = ControlProps & {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  min?: string;
  max?: string;
  type?: 'date' | 'datetime-local';
  clearable?: boolean;
};
function dateValue(value: string | undefined, withTime: boolean): DateValue | null {
  if (!value) return null;
  try {
    return withTime ? parseDateTime(value) : parseDate(value.slice(0, 10));
  } catch {
    return null;
  }
}

export const DatePicker = forwardRef<{ focus: () => void }, DatePickerProps>(function DatePicker(
  {
    id,
    name,
    className = '',
    style,
    value,
    defaultValue,
    onChange,
    onBlur,
    min,
    max,
    type = 'date',
    disabled,
    required,
    clearable = !required,
    validationBehavior = 'native',
    ...aria
  },
  ref,
) {
  const field = useContext(FormFieldContext);
  const input = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [container, setContainer] = useState<Element>();
  const withTime = type === 'datetime-local';
  const now = today('Asia/Shanghai');
  const placeholder = withTime ? new CalendarDateTime(now.year, now.month, now.day, 9) : now;
  const label = aria['aria-label'];
  const [changed, setChanged] = useState(0);
  const change = (next: DateValue | null) => {
    onChange?.(next ? next.toString().slice(0, withTime ? 16 : 10) : '');
    setChanged((n) => n + 1);
  };
  useEffect(() => {
    if (changed)
      input.current?.closest('form')?.dispatchEvent(new Event('change', { bubbles: true }));
  }, [changed]);
  useImperativeHandle(
    ref,
    () => ({
      focus: () => input.current?.querySelector<HTMLElement>('[role="spinbutton"]')?.focus(),
    }),
    [],
  );
  return (
    <I18nProvider locale="zh-CN">
      <AriaDatePicker
        id={id}
        className={`form-date ${className.replace(/\binput\b/g, '').trim()}`}
        style={style}
        aria-label={label}
        aria-labelledby={aria['aria-labelledby'] || (!label ? field?.labelId : undefined)}
        aria-describedby={aria['aria-describedby'] || field?.descriptionId}
        name={name}
        {...(value !== undefined
          ? { value: dateValue(value, withTime) }
          : { defaultValue: dateValue(defaultValue, withTime) })}
        onChange={change}
        onBlur={onBlur}
        minValue={dateValue(min, withTime) || undefined}
        maxValue={dateValue(max, withTime) || undefined}
        placeholderValue={placeholder}
        granularity={withTime ? 'minute' : 'day'}
        hourCycle={24}
        shouldForceLeadingZeros
        isDisabled={disabled}
        isRequired={required}
        isInvalid={
          aria['aria-invalid'] === true || aria['aria-invalid'] === 'true' || field?.invalid
        }
        validationBehavior={validationBehavior}
        isOpen={open}
        onOpenChange={(next) => {
          setContainer(trigger.current?.closest('[role="dialog"]') || undefined);
          setOpen(next);
        }}
      >
        {({ state }) => (
          <>
            <Group className="date-input-group">
              <DateInput ref={input} className="date-input">
                {(segment) => <DateSegment segment={segment} className="date-segment" />}
              </DateInput>
              {clearable && state.value && (
                <AriaButton
                  slot={null}
                  className="date-clear"
                  aria-label="清除日期"
                  isDisabled={disabled}
                  onPress={() => state.setValue(null)}
                >
                  <X size={14} />
                </AriaButton>
              )}
              <AriaButton ref={trigger} className="date-open" aria-label="打开日历">
                <CalendarIcon size={17} aria-hidden="true" />
              </AriaButton>
            </Group>
            <FieldError className="control-error" />
            <Popover
              className="control-popover date-popover"
              placement="bottom start"
              offset={6}
              UNSTABLE_portalContainer={container}
            >
              <Dialog className="date-dialog" aria-label="选择日期">
                <Suspense
                  fallback={
                    <div className="calendar-loading" role="status">
                      正在加载日历…
                    </div>
                  }
                >
                  <CalendarPanel
                    state={state}
                    withTime={withTime}
                    min={dateValue(min, withTime)}
                    max={dateValue(max, withTime)}
                  />
                </Suspense>
              </Dialog>
            </Popover>
          </>
        )}
      </AriaDatePicker>
    </I18nProvider>
  );
});
